import {Three as THREE, ThreeTsl as TSL} from "#three-texture-streaming/three-exports"
import type {TextureNodeLike} from "#three-texture-streaming/texture-node-like"

export class TextureThreeNode extends THREE.TempNode<"vec4"> implements TextureNodeLike {
    constructor(readonly textureNode: THREE.TextureNode) {
        super("vec4")
    }

    get uvNode(): THREE.Node<"vec2"> | null {
        return (this.textureNode.uvNode as THREE.Node<"vec2"> | null | undefined) ?? null
    }

    override generate(builder: THREE.NodeBuilder, output?: string | null): string | null | undefined {
        const type = this.getNodeType(builder)
        return this.textureNode.build(builder, output ?? type) as string | null
    }

    sample(uv: THREE.Node<"vec2">): THREE.Node<"vec4"> {
        return this.textureNode.sample(uv)
    }

    sampleGradient(uv: THREE.Node<"vec2">): {dFdU: THREE.Node<"vec4">; dFdV: THREE.Node<"vec4">} {
        const texSize = this.textureNode.size(TSL.int(0)) as THREE.Node<"ivec2">

        const texelStep = TSL.vec2(TSL.reciprocal(texSize.x.toFloat()), TSL.reciprocal(texSize.y.toFloat()))

        const sampleAt = (uvOffset: THREE.Node<"vec2">) => this.sample(uv.add(uvOffset)) as THREE.Node<"vec4">

        const s_px = sampleAt(TSL.vec2(texelStep.x, TSL.float(0)))
        const s_mx = sampleAt(TSL.vec2(texelStep.x.negate(), TSL.float(0)))
        const s_py = sampleAt(TSL.vec2(TSL.float(0), texelStep.y))
        const s_my = sampleAt(TSL.vec2(TSL.float(0), texelStep.y.negate()))

        const dFdU = s_px.sub(s_mx).div(texelStep.x.mul(TSL.float(2)))
        const dFdV = s_py.sub(s_my).div(texelStep.y.mul(TSL.float(2)))
        return {dFdU, dFdV}
    }
}
